import { ShippingPortalLocation, FmtpError } from '@lanterds/types';
import Grid from '@mui/material/Unstable_Grid2';
import {
    AutocompleteRenderGetTagProps,
    Chip,
    TextField,
    CircularProgress,
    Checkbox,
    FormControl,
    Autocomplete,
} from '@mui/material';
import React, { useRef, useState, useEffect } from 'react';
import { useErrorHandler } from 'react-error-boundary';
import { searchEndpoints } from 'src/api/shippingPortal';
import { LocationTypes, SearchTypes } from 'src/components/Views/ShippingPortal/types';
import { formatEndpointAddress } from 'src/util/formatters';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import useDebounce from 'src/hooks/useDebounce';

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

enum FieldTypes {
    Name = 'name',
    Code = 'code',
    Address = 'address',
}

type AddressPickerLocation = ShippingPortalLocation & {
    fmtpAddressPickerId?: string;
    formattedAddress?: string;
};

export type AddressPickerProps = {
    name: string;
    onChangeInput: (value: ShippingPortalLocation | ShippingPortalLocation[] | null, name: string) => void;
    locations: ShippingPortalLocation[];
    context: {
        locationType?: LocationTypes;
        origin?: ShippingPortalLocation | null;
        destination?: ShippingPortalLocation | null;
        destinations?: ShippingPortalLocation[] | null;
        labelType?: SearchTypes; // TODO: remove soon, for backwards compatability
        searchType: SearchTypes;
        filterByOrigin?: boolean;
    };
    enableAutoPopulate: boolean;
    customer?: { label: string; id: string } | null;
    disabled?: boolean;
    multiSelect?: boolean;
    fieldLabels?: Partial<Record<FieldTypes, string>>;
    splitNameAndCodeFields?: boolean;
    fieldErrors?: {
        nameError?: string;
        codeError?: string;
        addressError?: string;
    };
};

enum InputNames {
    NAME = 'name',
    CODE = 'dealerOrDockCode',
    ADDRESS = 'streetAddress',
}

export function AddressPicker({
    name,
    onChangeInput,
    locations,
    context,
    enableAutoPopulate,
    customer,
    disabled,
    multiSelect = false,
    fieldLabels = {},
    splitNameAndCodeFields = false,
    fieldErrors,
}: AddressPickerProps) {
    const handleError = useErrorHandler();

    const initialInputDetails = { value: '', name: '' };

    const currentRequestId = useRef(0);
    const endpointsRef = useRef<ShippingPortalLocation[]>([]);
    const [selectedAddresses, setSelectedAddresses] = useState<ShippingPortalLocation[]>(locations);
    const [isLoading, setIsLoading] = useState(false);
    const [nameOpen, setNameOpen] = useState(false);
    const [codeOpen, setCodeOpen] = useState(false);
    const [addressOpen, setAddressOpen] = useState(false);
    const [endpointsWithNames, setEndpointsWithNames] = useState<ShippingPortalLocation[]>([]);
    const [endpointsWithEndPointCode, setEndpointsWithEndPointCode] = useState<ShippingPortalLocation[]>([]);
    const [endpointsWithAddress, setEndpointsWithAddress] = useState<ShippingPortalLocation[]>([]);
    const [inputDetails, setInputDetails] = useState(initialInputDetails);

    const debouncedValue = useDebounce(inputDetails, initialInputDetails);

    useEffect(() => {
        if (enableAutoPopulate && !selectedAddresses.length && !isLoading) {
            getEndpoints().then((data: ShippingPortalLocation[]) => {
                if (data.length === 1) onChangeAddressPickerInput(multiSelect ? data : data[0]);
            });
        }
    }, [enableAutoPopulate, selectedAddresses]);

    useEffect(() => {
        if (locations) {
            setSelectedAddresses(locations);
        }
    }, [locations]);

    useEffect(() => {
        setSelectedAddresses([]);
        setEndpointsWithNames([]);
        setEndpointsWithEndPointCode([]);
        setEndpointsWithAddress([]);
    }, [customer]);

    useEffect(() => {
        const { name, value } = debouncedValue;

        if (name && value) {
            switch (name) {
                case InputNames.NAME:
                    getEndpointsWithName(value);
                    break;
                case InputNames.CODE:
                    getEndpointsWithEndPointCode(value);
                    break;
                case InputNames.ADDRESS:
                    getEndpointsWithAddress(value);
                    break;
                default:
                    break;
            }
        }
    }, [debouncedValue]);

    const onChangeAddressPickerInput = (value: ShippingPortalLocation | ShippingPortalLocation[] | null) => {
        onChangeInput(value, name);

        if (Array.isArray(value)) {
            setSelectedAddresses(value);
        } else {
            setSelectedAddresses(value ? [value] : []);
        }
    };

    const getEndpoints = async (val?: string, column?: string) => {
        setIsLoading(true);
        const thisRequestId = ++currentRequestId.current; //We do this so that we can eliminate a race condition populating with old data
        const filters = column ? { [column]: val } : {};
        try {
            const arg = { ...filters, ...(customer ? { customerFmtpId: customer.id } : null) };
            const endpoints = await searchEndpoints(arg, context);
            if (thisRequestId === currentRequestId.current) {
                if (multiSelect) {
                    endpointsRef.current = endpoints.data.map((location: ShippingPortalLocation) => ({
                        ...location,
                        fmtpAddressPickerId: `${location.id}-${location.locationCode}`,
                    }));
                } else {
                    endpointsRef.current = endpoints.data;
                }
            }

            setIsLoading(false);
        } catch (err) {
            const { message } = err as Error;
            handleError(new FmtpError({ message, errorCode: 'SHU010' }));
        }
        return endpointsRef.current;
    };

    const getEndpointsWithEndPointCode = async (val: string) => {
        const endpoints = await getEndpoints(val, 'new_endpointcode');
        setEndpointsWithEndPointCode(endpoints);
    };
    const getEndpointsWithName = async (val: string) => {
        const endpoints = await getEndpoints(val, 'name');
        setEndpointsWithNames(endpoints);
    };
    const getEndpointsWithAddress = async (val: string) => {
        const endpoints = await getEndpoints(val, 'address1_composite');
        setEndpointsWithAddress(endpoints);
    };

    const getValue = () => {
        if (multiSelect) {
            return selectedAddresses;
        } else {
            if (selectedAddresses && selectedAddresses.length) {
                return selectedAddresses[0];
            }

            return null;
        }
    };

    const renderTags = (
        value: any[],
        getTagProps: AutocompleteRenderGetTagProps,
        valueType: keyof (ShippingPortalLocation & { formattedAddress: string }),
    ) => {
        const numTags = value.length;
        const limitTags = 1;

        return (
            <>
                {value
                    .slice(0, limitTags)
                    .map((option: ShippingPortalLocation & { formattedAddress: string }, index: number) => (
                        <Chip {...getTagProps({ index })} key={index} label={option[valueType]} />
                    ))}

                {numTags > limitTags && ` +${numTags - limitTags}`}
            </>
        );
    };

    const getLabel = (type: FieldTypes) => {
        switch (type) {
            case FieldTypes.Name:
                return `${fieldLabels.name ?? 'Name'}`;
            case FieldTypes.Code:
                return `${fieldLabels.code ?? 'Dealer or Dock Code'}`;
            case FieldTypes.Address:
                return `${fieldLabels.address ?? 'Street Address'}`;
            default:
                return '';
        }
    };

    return (
        <>
            <Grid md={splitNameAndCodeFields ? 6 : 12} sm={12} xs={12}>
                <FormControl fullWidth>
                    <Autocomplete
                        multiple={multiSelect}
                        disableCloseOnSelect={multiSelect}
                        limitTags={1}
                        id={`${name}-name-input`}
                        open={nameOpen}
                        onOpen={() => {
                            setNameOpen(true);
                        }}
                        onClose={() => {
                            setNameOpen(false);
                            setEndpointsWithNames([]);
                            setInputDetails(initialInputDetails);
                        }}
                        options={endpointsWithNames}
                        getOptionLabel={(o) => o.name}
                        filterOptions={(x) => x}
                        selectOnFocus
                        clearOnBlur
                        handleHomeEndKeys
                        loading={isLoading}
                        isOptionEqualToValue={(option: AddressPickerLocation, value: AddressPickerLocation) => {
                            if (multiSelect) {
                                return option.fmtpAddressPickerId === value.fmtpAddressPickerId;
                            } else {
                                return option.id === value.id;
                            }
                        }}
                        renderTags={(value, getTagProps: AutocompleteRenderGetTagProps) =>
                            renderTags(value, getTagProps, 'name')
                        }
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                variant="outlined"
                                label={getLabel(FieldTypes.Name)}
                                name={InputNames.NAME}
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                }}
                                InputLabelProps={{ ...params.InputLabelProps }}
                                error={!!fieldErrors?.nameError}
                                helperText={fieldErrors?.nameError}
                            />
                        )}
                        renderOption={(props, option, { selected }) => {
                            if (multiSelect) {
                                return (
                                    <li {...props}>
                                        <Checkbox icon={icon} checkedIcon={checkedIcon} checked={selected} />
                                        {`${option.name} (${option.city}, ${option.state}, ${option.locationCode})`}
                                    </li>
                                );
                            } else {
                                return (
                                    <li {...props}>
                                        {`${option.name} (${option.city}, ${option.state}, ${option.locationCode})`}
                                    </li>
                                );
                            }
                        }}
                        value={getValue()}
                        onInputChange={(event, value) => {
                            // Event is null on init, so skip in that case
                            if (event && event.type === 'change' && value) {
                                const { name } = event.target as HTMLInputElement;
                                setInputDetails({ value, name });
                            } else {
                                if (!multiSelect && nameOpen) {
                                    setEndpointsWithNames([]);
                                }
                            }
                        }}
                        onChange={(_event, newValue) => {
                            onChangeAddressPickerInput(newValue);
                        }}
                        disabled={(isLoading && !nameOpen) || disabled}
                    />
                </FormControl>
            </Grid>
            <Grid md={splitNameAndCodeFields ? 6 : 12} sm={12} xs={12}>
                <FormControl fullWidth>
                    <Autocomplete
                        multiple={multiSelect}
                        disableCloseOnSelect={multiSelect}
                        limitTags={1}
                        id={`${name}-code-input`}
                        open={codeOpen}
                        onOpen={() => {
                            setCodeOpen(true);
                        }}
                        onClose={() => {
                            setCodeOpen(false);
                            setEndpointsWithEndPointCode([]);
                            setInputDetails(initialInputDetails);
                        }}
                        options={endpointsWithEndPointCode}
                        getOptionLabel={(o) => o.locationCode}
                        filterOptions={(x) => x}
                        selectOnFocus
                        clearOnBlur
                        handleHomeEndKeys
                        loading={isLoading}
                        isOptionEqualToValue={(option: AddressPickerLocation, value: AddressPickerLocation) => {
                            if (multiSelect) {
                                return option.fmtpAddressPickerId === value.fmtpAddressPickerId;
                            } else {
                                return option.id === value.id;
                            }
                        }}
                        renderTags={(value, getTagProps: AutocompleteRenderGetTagProps) =>
                            renderTags(value, getTagProps, 'locationCode')
                        }
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                variant="outlined"
                                label={getLabel(FieldTypes.Code)}
                                name={InputNames.CODE}
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                }}
                                InputLabelProps={{ ...params.InputLabelProps }}
                                error={!!fieldErrors?.codeError}
                                helperText={fieldErrors?.codeError}
                            />
                        )}
                        renderOption={(props, option, { selected }) => {
                            if (multiSelect) {
                                return (
                                    <li {...props}>
                                        <Checkbox icon={icon} checkedIcon={checkedIcon} checked={selected} />
                                        {option.locationCode}
                                    </li>
                                );
                            } else {
                                return <li {...props}>{option.locationCode}</li>;
                            }
                        }}
                        value={getValue()}
                        onInputChange={(event, value) => {
                            // Event is null on init, so skip in that case
                            if (event && event.type === 'change' && value) {
                                const { name } = event.target as HTMLInputElement;
                                setInputDetails({ value, name });
                            } else {
                                if (!multiSelect && nameOpen) {
                                    setEndpointsWithEndPointCode([]);
                                }
                            }
                        }}
                        onChange={(event, newValue) => {
                            onChangeAddressPickerInput(newValue);
                        }}
                        disabled={(isLoading && !codeOpen) || disabled}
                    />
                </FormControl>
            </Grid>

            <Grid xs={12}>
                <FormControl fullWidth>
                    <Autocomplete
                        multiple={multiSelect}
                        disableCloseOnSelect={multiSelect}
                        limitTags={1}
                        id={`${name}-address-input`}
                        open={addressOpen}
                        onOpen={() => {
                            setAddressOpen(true);
                        }}
                        onClose={() => {
                            setAddressOpen(false);
                            setEndpointsWithAddress([]);
                            setInputDetails(initialInputDetails);
                        }}
                        options={endpointsWithAddress}
                        getOptionLabel={(o) => formatEndpointAddress(o as ShippingPortalLocation, false, false, false)}
                        filterOptions={(x) => x}
                        selectOnFocus
                        clearOnBlur
                        handleHomeEndKeys
                        loading={isLoading}
                        isOptionEqualToValue={(option: AddressPickerLocation, value: AddressPickerLocation) => {
                            if (multiSelect) {
                                return option.fmtpAddressPickerId === value.fmtpAddressPickerId;
                            } else {
                                return option.id === value.id;
                            }
                        }}
                        renderTags={(value, getTagProps: AutocompleteRenderGetTagProps) =>
                            renderTags(value, getTagProps, 'formattedAddress')
                        }
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                variant="outlined"
                                label={getLabel(FieldTypes.Address)}
                                name={InputNames.ADDRESS}
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                }}
                                InputLabelProps={{ ...params.InputLabelProps }}
                                error={!!fieldErrors?.addressError}
                                helperText={fieldErrors?.addressError}
                            />
                        )}
                        renderOption={(props, option: AddressPickerLocation, { selected }) => {
                            if (multiSelect) {
                                return (
                                    <li {...props}>
                                        <Checkbox icon={icon} checkedIcon={checkedIcon} checked={selected} />
                                        {option.formattedAddress}
                                    </li>
                                );
                            } else {
                                return <li {...props}>{option.formattedAddress}</li>;
                            }
                        }}
                        value={getValue()}
                        onInputChange={(event, value) => {
                            // Event is null on init, so skip in that case
                            if (event && event.type === 'change' && value) {
                                const { name } = event.target as HTMLInputElement;
                                setInputDetails({ value, name });
                            } else {
                                if (!multiSelect && nameOpen) {
                                    setEndpointsWithAddress([]);
                                }
                            }
                        }}
                        onChange={(_event, newValue) => {
                            onChangeAddressPickerInput(newValue);
                        }}
                        disabled={(isLoading && !addressOpen) || disabled}
                    />
                </FormControl>
            </Grid>
        </>
    );
}
